// Simplified ProjectService for Vite migration POC
// Only includes methods needed for select-project page

import { http } from 'src/utils/http.util'
import type { TProject } from '../types/project'
import type { TContent } from 'src/pages/configuration/contents/_components/content.type'
import type {
  TProgrammedMessage,
  TProgrammedMessageForAPI,
} from 'src/pages/configuration/followups/[followupId]/messages/_components/programmed-message.type'
import type { TFollowupTimeline } from 'src/pages/configuration/followups/[followupId]/_components/followup-timeline.type'
import type { TVariable } from 'src/types/variable'
import type { TMedAlert } from 'src/types/med-alert'
import type { TNewVariableFormValues } from 'src/pages/configuration/followups/[followupId]/chatbot/[chatbotId]/_components/chatbot-form/variable/new-variable-form.component'
import type {
  TPermission,
  TPermissionFormValues,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import type { TUploadConnector } from 'src/pages/medical/upload/_components/upload-connector.type'
import type {
  TBatchImportStatusResponse,
  TBatchImportSummary,
} from 'src/pages/medical/upload/_components/batch-import.type'

export type TCreateMedAlertPayload = Pick<TMedAlert, 'intlLabel' | 'ui' | 'type'>

// Type for project list (simplified from Next.js)
export type TProjectList = Array<
  Pick<TProject, 'id' | 'name'> & {
    smsSender?: string
    usersCount?: number
    patientsCount?: number
  }
>

export const ProjectService = {
  getProjectsList: (): Promise<TProjectList> => {
    return http.get('/projects/list/').then((res: any) => res.data)
  },

  getById: (id: number): Promise<TProject> => {
    return http.get(`/projects/${id}/`).then((res: any) => res.data)
  },

  getProjectLocations: (projectId: number): Promise<any[]> => {
    return http.get(`/projects/${projectId}/locations`).then((res: any) => res.data)
  },

  getDocuments: (projectId: number, includeGlobal: boolean): Promise<any[]> => {
    return http
      .get(`/projects/${projectId}/documents?includeGlobal=${!!includeGlobal}`)
      .then((res) => res.data)
  },

  getInteropMappings: (projectId: number): Promise<any[]> => {
    return http.get(`projects/${projectId}/interop/mappings`).then((res) => res.data)
  },

  getAmslerGridImage: (variableId: any, patientFormId: any, patientId: any, projectId: any) => {
    return http.get(
      `projects/${projectId}/patients/${patientId}/patient-forms/${patientFormId}/amsler-grid/${variableId}`,
    )
  },

  getPatientFormDocument: (
    variableId: number | undefined,
    patientFormId: number | undefined,
    patientId: string | number,
    projectId: string | number,
  ) => {
    return http.get(
      `projects/${projectId}/patients/${patientId}/patient-forms/${patientFormId}/document/${variableId}`,
      { responseType: 'blob' },
    )
  },

  getPatientFollowupDocuments: (
    patientFollowupId: number,
    patientId: string | number,
    projectId: string | number,
  ) => {
    return http
      .get(`projects/${projectId}/patients/${patientId}/followups/${patientFollowupId}/documents`)
      .then((response) => response.data)
  },

  getStandalonePatientVariableDocument: (
    variableId: number,
    patientId: string | number,
    projectId: string | number,
  ) => {
    return http.get(
      `projects/${projectId}/patients/${patientId}/patient-forms/standalone-document/${variableId}`,
      { responseType: 'blob' },
    )
  },

  setDocumentWaived: (
    patientFollowupId: number,
    variableId: number,
    isWaived: boolean,
    patientId: string | number,
    projectId: string | number,
  ) => {
    return http.patch(
      `projects/${projectId}/patients/${patientId}/followups/${patientFollowupId}/documents/${variableId}/waive`,
      { isWaived },
    )
  },

  getStatistics: (id: number) => {
    return http.get(`/projects/${id}/statistics`).then((result) => result.data)
  },

  getPatientFormsCsvBlob: (projectId: number) => {
    return http.get(`projects/${projectId}/statistics/patient-form-csv`, {
      responseType: 'blob',
    })
  },

  getPatientMedEventXlsxBlob: (
    projectId: number,
    medEventIds: number[],
    minDate?: string | null,
    maxDate?: string | null,
  ) => {
    const params: Record<string, any> = { medEventIds }
    if (minDate) params.minDate = minDate
    if (maxDate) params.maxDate = maxDate
    return http.get(`projects/${projectId}/statistics/patient-med-event-xlsx`, {
      params,
      responseType: 'blob',
    })
  },

  getSelfConfigurationContents: (projectId: number): Promise<TContent[]> => {
    return http.get(`projects/${projectId}/self-configuration/content`).then((res: any) => res.data)
  },

  getSelfConfigurationContent: (projectId: number, contentId: number): Promise<TContent> => {
    return http
      .get(`projects/${projectId}/self-configuration/content/${contentId}`)
      .then((res: any) => res.data)
  },

  postSelfConfigurationContent: (
    projectId: number,
    formData: Omit<TContent, 'id' | 'origin'>,
  ): Promise<TContent> => {
    return http
      .post(`projects/${projectId}/self-configuration/content`, formData)
      .then((res: any) => res.data)
  },

  patchSelfConfigurationContent: (
    projectId: number,
    contentId: number,
    formData: Omit<TContent, 'origin'>,
  ): Promise<TContent> => {
    return http
      .patch(`projects/${projectId}/self-configuration/content/${contentId}`, formData)
      .then((res: any) => res.data)
  },

  getSelfConfigurationMessages: (projectId: number): Promise<TProgrammedMessage[]> => {
    return http.get(`projects/${projectId}/self-configuration/message`).then((res: any) => res.data)
  },

  getSelfConfigurationMessage: (projectId: number, messageId: number): Promise<TProgrammedMessage> => {
    return http
      .get(`projects/${projectId}/self-configuration/message/${messageId}`)
      .then((res: any) => res.data)
  },

  postSelfConfigurationMessage: (
    projectId: number,
    formData: TProgrammedMessageForAPI,
  ): Promise<TProgrammedMessage> => {
    return http
      .post(`projects/${projectId}/self-configuration/message`, formData)
      .then((res: any) => res.data)
  },

  putSelfConfigurationMessage: (
    projectId: number,
    messageId: number,
    formData: TProgrammedMessageForAPI,
  ): Promise<TProgrammedMessage> => {
    return http
      .put(`projects/${projectId}/self-configuration/message/${messageId}`, formData)
      .then((res: any) => res.data)
  },

  deleteSelfConfigurationMessage: (projectId: number, messageId: number) => {
    return http.delete(`projects/${projectId}/self-configuration/message/${messageId}`)
  },

  cloneSelfConfigurationMessage: (
    projectId: number,
    messageId: number,
    payload: { targetProjectId: number; targetFollowupId: number; medEventIds?: number[] },
  ): Promise<{
    id: number
    label: string
    targetProjectId: number
    targetFollowupId: number
  }> => {
    return http
      .post(`projects/${projectId}/self-configuration/message/${messageId}/clone`, payload)
      .then((res: any) => res.data)
  },

  getVariables: (projectId: number): Promise<TVariable[]> => {
    return http.get(`/projects/${projectId}/variables`).then((res: any) => res.data)
  },

  postVariable: (projectId: number, data: TNewVariableFormValues): Promise<TVariable> => {
    return http.post(`/projects/${projectId}/variables`, data).then((res: any) => res.data)
  },

  getMedAlerts: (projectId: number): Promise<TMedAlert[]> => {
    return http.get(`/projects/${projectId}/med-alerts`).then((res: any) => res.data)
  },

  postMedAlert: (projectId: number, data: TCreateMedAlertPayload): Promise<TMedAlert> => {
    return http.post(`/projects/${projectId}/med-alerts`, data).then((res: any) => res.data)
  },

  getSelfConfigurationFollowup: (projectId: number, followupId: string): Promise<TFollowupTimeline> => {
    return http
      .get(`projects/${projectId}/self-configuration/followup/${followupId}`)
      .then((res: any) => res.data)
  },

  getPatientMedEventCsvBlob: (
    projectId: number,
    medEventIds: number[],
    minDate?: string | null,
    maxDate?: string | null,
  ) => {
    const params: Record<string, any> = { medEventIds }
    if (minDate) params.minDate = minDate
    if (maxDate) params.maxDate = maxDate
    return http.get(`projects/${projectId}/statistics/patient-med-event-csv`, {
      params,
      responseType: 'blob',
    })
  },

  getPermissions: (projectId: number): Promise<TPermission[]> => {
    return http.get<TPermission[]>(`projects/${projectId}/permissions`).then((res) => res.data)
  },

  getPermission: (projectId: number, permissionId: number): Promise<TPermission> => {
    return http.get<TPermission>(`projects/${projectId}/permissions/${permissionId}`).then((res) => res.data)
  },

  patchPermission: (
    projectId: number,
    permissionId: number,
    permission: TPermissionFormValues,
  ): Promise<TPermission> => {
    return http
      .patch<TPermission>(`projects/${projectId}/permissions/${permissionId}`, permission)
      .then((res) => res.data)
  },

  postPermission: (projectId: number, permission: TPermissionFormValues): Promise<TPermission> => {
    return http.post<TPermission>(`projects/${projectId}/permissions`, permission).then((res) => res.data)
  },

  deletePermission: (projectId: number, permissionId: number): Promise<TPermission> => {
    return http
      .delete<TPermission>(`projects/${projectId}/permissions/${permissionId}`)
      .then((res) => res.data)
  },

  getUploadConnectors: (projectId: number): Promise<TUploadConnector[]> => {
    return http.get(`/interop/projects/${projectId}/upload-connectors`).then((res) => res.data)
  },

  postUploadConnectorFile: (projectId: number, slug: string, formData: FormData) => {
    return http.post(`/interop/projects/${projectId}/upload-connectors/${slug}`, formData)
  },

  getBatchImports: (projectId: number): Promise<TBatchImportSummary[]> => {
    return http.get(`/interop/projects/${projectId}/batch-imports`).then((res) => res.data)
  },

  getBatchImportStatus: (
    projectId: number,
    batchImportToken: string,
  ): Promise<TBatchImportStatusResponse> => {
    return http
      .get(`/interop/projects/${projectId}/batch-imports/${encodeURIComponent(batchImportToken)}/status`)
      .then((res) => res.data)
  },
}
